import React, { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import './PackageManager.css';

const PackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // New package form state
  const [newPackage, setNewPackage] = useState({
    name: '',
    duration: '',
    price: '',
    originalPrice: '',
    region: 'tr',
    popular: false,
    users: '',
    features: []
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      const packagesRef = collection(db, 'packages');
      const q = query(packagesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const packagesData = [];
      querySnapshot.forEach((doc) => {
        packagesData.push({ id: doc.id, ...doc.data() });
      });
      
      setPackages(packagesData);
    } catch (err) {
      console.error('Load packages error:', err);
      // Error will be handled by parent component
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      setLoading(true);
      const packageRef = doc(db, 'packages', id);
      await updateDoc(packageRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      
      await loadPackages();
      setEditingId(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Update error:', err);
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: '❌ Güncelleme hatası: ' + err.message, type: 'error' } 
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu paketi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'packages', id));
      await loadPackages();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: '✅ Paket başarıyla silindi', type: 'success' } 
      }));
    } catch (err) {
      console.error('Delete error:', err);
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: '❌ Silme hatası: ' + err.message, type: 'error' } 
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newPackage.name || !newPackage.duration || !newPackage.price) {
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: '⚠️ Lütfen en azından paket adı, süre ve fiyat bilgilerini doldurun', type: 'warning' } 
      }));
      return;
    }

    try {
      setLoading(true);
      await addDoc(collection(db, 'packages'), {
        ...newPackage,
        features: newPackage.features || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      await loadPackages();
      setIsAdding(false);
      setNewPackage({
        name: '',
        duration: '',
        price: '',
        originalPrice: '',
        region: 'tr',
        popular: false,
        users: '',
        features: []
      });
      setNewFeature('');
      await loadPackages();
      setIsAdding(false);
      setNewPackage({
        name: '',
        duration: '',
        price: '',
        originalPrice: '',
        region: 'tr',
        popular: false,
        users: '',
        features: []
      });
      setNewFeature('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: '✅ Paket başarıyla eklendi', type: 'success' } 
      }));
    } catch (err) {
      console.error('Add error:', err);
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: '❌ Ekleme hatası: ' + err.message, type: 'error' } 
      }));
    } finally {
      setLoading(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setNewPackage({
        ...newPackage,
        features: [...(newPackage.features || []), newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    const newFeatures = [...(newPackage.features || [])];
    newFeatures.splice(index, 1);
    setNewPackage({ ...newPackage, features: newFeatures });
  };

  return (
    <div className="package-manager">
      <div className="manager-header">
        <h2>📦 Paket Yönetimi</h2>
        <div className="header-actions">
          <button 
            onClick={() => setIsAdding(!isAdding)} 
            className="btn-add"
            disabled={loading}
          >
            {isAdding ? '❌ İptal' : '➕ Yeni Paket Ekle'}
          </button>
          {saved && <span className="saved-badge">✅ Kaydedildi!</span>}
        </div>
      </div>

      {isAdding && (
        <div className="add-package-form">
          <h3>Yeni Paket Ekle</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Paket Adı *</label>
              <input
                type="text"
                value={newPackage.name}
                onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                placeholder="Örn: 6 AY PAKET"
              />
            </div>
            <div className="form-group">
              <label>Süre *</label>
              <input
                type="text"
                value={newPackage.duration}
                onChange={(e) => setNewPackage({ ...newPackage, duration: e.target.value })}
                placeholder="Örn: 6 Ay"
              />
            </div>
            <div className="form-group">
              <label>Fiyat *</label>
              <input
                type="text"
                value={newPackage.price}
                onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
                placeholder="Örn: 1.300 TL veya €45"
              />
            </div>
            <div className="form-group">
              <label>Eski Fiyat</label>
              <input
                type="text"
                value={newPackage.originalPrice}
                onChange={(e) => setNewPackage({ ...newPackage, originalPrice: e.target.value })}
                placeholder="Örn: 2.000 TL"
              />
            </div>
            <div className="form-group">
              <label>Bölge</label>
              <select
                value={newPackage.region}
                onChange={(e) => setNewPackage({ ...newPackage, region: e.target.value })}
              >
                <option value="tr">TR</option>
                <option value="eu">EU</option>
              </select>
            </div>
            <div className="form-group">
              <label>Kullanıcı Sayısı</label>
              <input
                type="text"
                value={newPackage.users}
                onChange={(e) => setNewPackage({ ...newPackage, users: e.target.value })}
                placeholder="Örn: 1 Kullanıcı"
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={newPackage.popular}
                  onChange={(e) => setNewPackage({ ...newPackage, popular: e.target.checked })}
                />
                Popüler Paket Olarak İşaretle
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Özellikler</label>
            <div className="features-input">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                placeholder="Özellik ekleyin ve Enter'a basın"
              />
              <button onClick={addFeature} className="btn-add-feature">➕ Ekle</button>
            </div>
            {newPackage.features && newPackage.features.length > 0 && (
              <div className="features-list">
                {newPackage.features.map((feature, index) => (
                  <span key={index} className="feature-tag">
                    {feature}
                    <button onClick={() => removeFeature(index)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <button onClick={handleAdd} className="btn-save" disabled={loading}>
            {loading ? '⏳ Kaydediliyor...' : '💾 Paketi Kaydet'}
          </button>
        </div>
      )}

      <div className="packages-list">
        {loading && packages.length === 0 ? (
          <div className="loading-state">
            <p>⏳ Paketler yükleniyor...</p>
          </div>
        ) : packages.length === 0 ? (
          <div className="empty-state">
            <p>📦 Henüz paket eklenmemiş.</p>
            <p>Yukarıdaki "Yeni Paket Ekle" butonuna tıklayarak paket ekleyebilirsiniz.</p>
          </div>
        ) : (
          packages.map(pkg => (
            <PackageItem
              key={pkg.id}
              pkg={pkg}
              editingId={editingId}
              setEditingId={setEditingId}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              loading={loading}
            />
          ))
        )}
      </div>
    </div>
  );
};

const PackageItem = ({ pkg, editingId, setEditingId, onUpdate, onDelete, loading }) => {
  const [editData, setEditData] = useState({
    name: pkg.name || '',
    duration: pkg.duration || '',
    price: pkg.price || '',
    originalPrice: pkg.originalPrice || '',
    region: pkg.region || 'tr',
    popular: pkg.popular || pkg.is_popular || false,
    users: pkg.users || '',
    features: pkg.features || []
  });
  const [editFeature, setEditFeature] = useState('');

  const handleSave = () => {
    onUpdate(pkg.id, editData);
  };

  const addEditFeature = () => {
    if (editFeature.trim()) {
      setEditData({
        ...editData,
        features: [...(editData.features || []), editFeature.trim()]
      });
      setEditFeature('');
    }
  };

  const removeEditFeature = (index) => {
    const newFeatures = [...(editData.features || [])];
    newFeatures.splice(index, 1);
    setEditData({ ...editData, features: newFeatures });
  };

  if (editingId === pkg.id) {
    return (
      <div className="package-item editing">
        <div className="edit-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Paket Adı</label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Süre</label>
              <input
                type="text"
                value={editData.duration}
                onChange={(e) => setEditData({ ...editData, duration: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Fiyat</label>
              <input
                type="text"
                value={editData.price}
                onChange={(e) => setEditData({ ...editData, price: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Eski Fiyat</label>
              <input
                type="text"
                value={editData.originalPrice}
                onChange={(e) => setEditData({ ...editData, originalPrice: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Bölge</label>
              <select
                value={editData.region}
                onChange={(e) => setEditData({ ...editData, region: e.target.value })}
              >
                <option value="tr">TR</option>
                <option value="eu">EU</option>
              </select>
            </div>
            <div className="form-group">
              <label>Kullanıcı Sayısı</label>
              <input
                type="text"
                value={editData.users}
                onChange={(e) => setEditData({ ...editData, users: e.target.value })}
              />
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={editData.popular}
                  onChange={(e) => setEditData({ ...editData, popular: e.target.checked })}
                />
                Popüler
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Özellikler</label>
            <div className="features-input">
              <input
                type="text"
                value={editFeature}
                onChange={(e) => setEditFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addEditFeature()}
                placeholder="Özellik ekle"
              />
              <button onClick={addEditFeature} className="btn-add-feature">➕</button>
            </div>
            {editData.features && editData.features.length > 0 && (
              <div className="features-list">
                {editData.features.map((feature, index) => (
                  <span key={index} className="feature-tag">
                    {feature}
                    <button onClick={() => removeEditFeature(index)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="edit-actions">
            <button onClick={handleSave} className="btn-save" disabled={loading}>
              💾 Kaydet
            </button>
            <button onClick={() => setEditingId(null)} className="btn-cancel">
              ❌ İptal
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="package-item">
      <div className="package-info">
        <h3>{pkg.name}</h3>
        <div className="package-details">
          <span className={`badge ${pkg.region === 'tr' ? 'badge-tr' : 'badge-eu'}`}>
            {pkg.region === 'tr' ? 'TR' : 'EU'}
          </span>
          <span>{pkg.duration}</span>
          {pkg.users && <span>{pkg.users}</span>}
          {(pkg.popular || pkg.is_popular) && <span className="badge popular">⭐ Popüler</span>}
        </div>
        {pkg.features && pkg.features.length > 0 && (
          <div className="package-features">
            {pkg.features.slice(0, 3).map((feature, idx) => (
              <span key={idx} className="feature-tag-small">✓ {feature}</span>
            ))}
            {pkg.features.length > 3 && <span className="feature-tag-small">+{pkg.features.length - 3} daha</span>}
          </div>
        )}
      </div>
      <div className="package-pricing">
        <div className="price-display">
          <div className="price">{pkg.price}</div>
          {pkg.originalPrice && (
            <div className="original-price">{pkg.originalPrice}</div>
          )}
        </div>
        <div className="package-actions">
          <button onClick={() => setEditingId(pkg.id)} className="btn-edit">
            ✏️ Düzenle
          </button>
          <button onClick={() => onDelete(pkg.id)} className="btn-delete" disabled={loading}>
            🗑️ Sil
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageManager;
